import {
  LitElement,
  css,
  html,
  classMap,
  styleMap,
  ref,
  createRef,
  unsafeCSS,
} from './-global.min.js'

class HHDetailsModal extends LitElement {
  static properties = {
    breakpoint: {
      type: String,
    },
    label: {
      type: String,
    },
    selector: {
      type: String,
    },
    open: {
      type: Boolean,
      attribute: 'is-open',
      reflect: true,
    },
    forcedOpen: {
      attribute: 'open',
    },
    lockHeight: {
      type: Boolean,
      attribute: 'lock-height',
    },
    heightOffset: {
      type: Number,
      attribute: 'height-offset',
    },
    _height: {
      state: true,
    },
    lockScroll: {
      type: Boolean,
      attribute: 'lock-scroll',
    },
    zIndex: {
      type: Number,
      attribute: 'z-index'
    },
    bodyClasses: {
      type: String,
      attribute: 'body-class'
    }
  }

  $container = createRef()
  $details = createRef()

  constructor() {
    super()

    this.open = false
    this.lockHeight = false
    this.lockScroll = false
    this.heightOffset = 3
    this.zIndex = '100'
    this.breakpoint = '(min-width: 768px)'
    this.bodyClasses = ''
    this.bodyListener = this.bodyListener.bind(this)
    this.closeWithEscape = this.closeWithEscape.bind(this)
    this.ctx = this.getAttribute('ctx')
  }

  connectedCallback() {
    super.connectedCallback()
    document.body.addEventListener('keyup', this.closeWithEscape)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.body.removeEventListener('keyup', this.closeWithEscape)
  }

  interceptEvent(e) {
    e._intercepted = true
  }

  toggle(e = false, value = undefined) {
    if (e.type === 'keyup' && e.code.toUpperCase() !== 'ENTER') {
      return true
    }

    e && e.preventDefault()

    if (this.open) {
      this.shadowRoot.querySelector('.content').classList.remove('has_animation')
      setTimeout(() => {
        this.open = typeof value === 'undefined' ? !this.open : value
        this[`${this.open ? 'add' : 'remove'}BodyListener`]()
        this.open && this.lockHeight && this.setHeight()
        this.open && this.broadcast()

        if (this.ctx == 'mobilemenu')
          document.body.classList.remove('mobile-menu--opened')
      }, 500)
    } else {
      this.open = typeof value === 'undefined' ? !this.open : value
      this[`${this.open ? 'add' : 'remove'}BodyListener`]()
      this.open && this.lockHeight && this.setHeight()
      this.open && this.broadcast()
      setTimeout(() => {
        this.shadowRoot.querySelector('.content').classList.add('has_animation')
      }, 0);

      if (this.ctx == 'mobilemenu')
        document.body.classList.add('mobile-menu--opened')
    }
  }

  closeFromDetails(e) {
    var path = (e.composedPath && e.composedPath()) || e.path
    if (
      path && path.some((element) => {
        return element.matches && element.matches('[data-close]')
      })
    ) {
      if (e.target.closest('hh-details-modal').querySelector('details')?.hasAttribute('open')) {
        e.target.closest('hh-details-modal').querySelector('details').removeAttribute('open');
      }
      this.toggle()
      document.querySelector('.mobile-menu__icon #reverse')?.beginElement()
    }
  }

  closeWithEscape(e) {
    if (e.code.toUpperCase() === 'ESCAPE' && this.open) {
      this.toggle()
    }
  }

  isMobile() {
    return !window.matchMedia(this.breakpoint).matches
  }

  addBodyListener() {
    if (this.lockScroll) {
      document.body.classList.add('overflow-hidden')
    }
    if (this.bodyClasses !== '') {
      document.body.classList.add(this.bodyClasses)
    }
  }

  removeBodyListener() {
    if (this.lockScroll) {
      document.body.classList.remove('overflow-hidden')
    }
    if (this.bodyClasses !== '') {
      document.body.classList.remove(this.bodyClasses)
    }
  }

  bodyListener(e) {
    !e._intercepted && this.toggle()
  }

  setHeight() {
    const { top: parentTop = 0 } = this.parentNode.getBoundingClientRect() || {}
    const { top: detailsTop = 0 } =
      this.$details?.value.getBoundingClientRect() || {}

    if (this.isMobile()) {
      if (this.ctx == 'mobilemenu') {
        const header = document.querySelector('.header-wrapper')
        if (header?.classList.contains('hh-sticky-header--hide-announcement')) {
          this._height = `var(--hh-window-height, 100dvh)`
        } else {
          this._height = `calc(var(--hh-window-height, 100dvh) - var(--hh-announcment-bar-height, 0))`
        }
      } else {
        this._height = `calc(var(--hh-window-height) - var(--hh-header-height))`
      }
    }
  }

  broadcast() {
    this.dispatchEvent(
      new CustomEvent('modalOpened', {
        bubbles: true,
        composed: true,
        detail: {
          value: this,
        },
      })
    )
  }

  toggleMaxWidth(e) {
    this.setAttribute('lock-max-width', e.detail?.value || false)
  }

  render() {
    return html` <!-- @ts-ignore no-incompatible-type-binding -->
      <style>
        :host {
          z-index: var(--hh-z-index, ${unsafeCSS(this.zIndex)});
        }
      </style>
      <div
        ?open="${this.open}"
        @click="${this.interceptEvent}"
        @toggleMaxWidth="${this.toggleMaxWidth}"
        ${ref(this.$container)}
      >
        <summary
          @keyup="${this.toggle}"
          aria-expanded="${this.open}"
          aria-haspopup="dialog"
          role="button"
          ${ref(this.$container)}
        >
          <slot
            @click="${this.toggle}"
            class="${classMap({ 'is-open': this.open })}"
            name="summary"
            tabindex="-1"
          ></slot>
        </summary>
        <div
          class="content"
          role="dialog"
          aria-modal="true"
          aria-label="${this.label}"
          style="${styleMap({ height: this._height })}"
          ${ref(this.$details)}
        >
          <div
            class="overlay"
            @click="${this.toggle}"
          ></div>
          <div class="content-inner">
            <hh-focus-trap active="${this.open}">
              <slot
                name="details"
                @keyup="${this.closeWithEscape}"
                @click="${this.closeFromDetails}"
              >
            </hh-focus-trap>
          </div>
        </div>
      </div>`
  }

  static get styles() {
    return css`
      summary {
        list-style-type: none;
      }

      summary:focus {
        outline: 0.1rem solid var(--hh-color-focus);
        outline-offset: var(--hh-focus-outline-offset);
      }

      summary:focus:not(:focus-visible) {
        outline: 0;
      }

      summary::-webkit-details-marker {
        display: none;
      }

      .content {
        position: var(--hh-position, absolute);
        top: var(--hh-top);
        left: 0;
        right: 0;
        bottom: 0;
        background: transparent;
        height: var(--hh-modal-height);
        z-index: var(--hh-z-index-inner, 1);
        visibility: hidden;
      }

      div[open] .content {
        visibility: visible;
      }

      .content-inner {
        background: var(--hh-modal-background);
        height: 100%;
        position: relative
      }

      :host([lock-max-width='true']) .content-inner {
        max-width: var(--hh-modal-max-width, 23rem);
      }

      .overlay {
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        background: var(--hh-modal-overlay-background, transparent);
        -webkit-backdrop-filter: var(--hh-modal-overlay-blur, none);
        backdrop-filter: var(--hh-modal-overlay-blur, none);
        display: block;
      }

      :host([ctx='mobilemenu']) .overlay {
        top: calc(var(--hh-header-inner-height) - var(--hh-announcment-bar-height));
      }

      .has_animation {
        -webkit-animation: var(--hh-animation, unset);
        animation: var(--hh-animation, unset);
      }

      details[open] + .content {
        pointer-events: unset;
      }

      ::slotted([slot='details']) {
        width: 100%;
        display: flex;
      }

      :host([ctx='mobilemenu']) .content {
        position: fixed;
        z-index: -1;
        transform: translate(-100%);
        transition: transform var(--hh-duration-long);
      }

      :host([ctx='mobilemenu']) .content.has_animation {
        transform: translate(0);
      }

      :host([ctx='mobilemenu']) .content-inner {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        padding-top: calc(var(--hh-header-inner-height) - var(--hh-announcment-bar-height));
      }

      @media screen and (min-width: 750px) {
        :host([ctx='filters']) .content {
          width: 60rem;
          max-width: 60rem;
          left: unset;
          right: 0;
        }

        :host([ctx='search']) .content {
          top: 80%;
          opacity: 0;
          transition: top 500ms cubic-bezier(0.17, 0.04, 0.03, 0.94),
            visibility 250ms ease, opacity 250ms ease;
        }

        :host([ctx='search']) .content.has_animation {
          opacity: 1;
          top: 100%;
          transition: top 500ms cubic-bezier(0.17, 0.04, 0.03, 0.94),
            visibility 250ms ease, opacity 250ms ease;
        }
      }
    `
  }
}

customElements.define('hh-details-modal', HHDetailsModal)
